<template>
  <div class="size-consolidated-variant-wrapper grid-y">
    <ColorSwatches
      v-if="!isMattressesCategory"
      :id="props.id"
      :key="key"
      :class="spacingClass"
      :color-props="{...colorProps, priceToColors, priceToColorsExp}"
      :price-to-colors="priceToColors"
      :price-to-colors-exp="priceToColorsExp"
    />

    <template v-if="isTypeDriven">
      <ProductType
        v-if="types.length"
        :id="id"
        v-model="selectedTypeId"
        :types="types"
        :is-spacing-enabled="isSpacingExperimentEnabled"
        label="Type"
        :class="spacingClass"
        @update:modelValue="onTypeChange"
      />

      <SizeSelection
        :key="forceSizeRerender"
        v-model="selectedSizeId"
        v-model:touched="touched"
        v-bind="sizeProps"
        :product-id="id"
        :name="`cta-sizes-${id}`"
        :is-size-dropdown="false"
        :is-size-chart="isSizeChartForConsolidated"
        :is-spacing-enabled="isSpacingExperimentEnabled"
        :size-chart-img="selectedConsolidatedSizeChartImage"
        :size-chart-id="selectedConsolidatedSizeChartId"
        :brand-name="brandName"
        :type-name="typeName"
        :experiment-flags="experimentFlagsObj"
        :class="spacingClass"
        @update:modelValue="onSizeChange"
      />
    </template>

    <template v-else>
      <SizeSelection
        v-model="selectedSizeId"
        v-model:touched="touched"
        v-bind="sizeProps"
        :product-id="id"
        :name="`cta-sizes-${id}`"
        :is-size-dropdown="false"
        :is-size-chart="isSizeChartForConsolidated"
        :is-spacing-enabled="isSpacingExperimentEnabled"
        :size-chart-img="selectedConsolidatedSizeChartImage"
        :size-chart-id="selectedConsolidatedSizeChartId"
        :brand-name="brandName"
        :type-name="typeName"
        :experiment-flags="experimentFlagsObj"
        :class="spacingClass"
        @update:modelValue="onSizeChange"
      />
      <ProductType
        v-if="types.length"
        :id="id"
        v-model="selectedTypeId"
        :types="types"
        :is-spacing-enabled="isSpacingExperimentEnabled"
        label="Type"
        :class="spacingClass"
        @update:modelValue="onTypeChange"
      />
    </template>
  </div>
</template>

<script setup>
/* istanbul ignore file */
import { useStore } from 'vuex';
import { ref, computed, watch } from 'vue';
import { useUpc } from '@common-vue/media-card-large';
import { SizeSelection } from '@common-vue/media-card-large/src/modules/size';
import { useTraitAnalyticsData } from '@common-vue/media-card-large/src/composables/useTraitAnalyticsData';
import { ProductType } from '@common-vue/media-card-large/src/modules/type';
import ColorSwatches from '@components/ColorSwatches';
import { useBrand } from '@composables/useBrand';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useExperiments } from '@composables/useExperiments';
import useConsolidated3rdVariant from '@modules/third-variant/composables/useConsolidated3rdVariant';
import useMembersOfProduct from '@composables/useMembersOfProduct';
import { useTopLevelCategoryFlags } from '@modules/featureControl';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  product: {
    /** @type import('vue').PropType<Product> */
    type: Object,
    required: true,
  },
  productPricingState: {
    type: Array,
    required: true,
  },
});

const { isMcom } = useBrand();
const { brandName, typeName } = useCommonProductGetters();
const { isMattressesCategory } = useTopLevelCategoryFlags();
const {
  sizeProps, selectedSizeId, types, selectedTypeId, isTypeDriven, colorProps, priceToColors, priceToColorsExp, firstUpcKeyForColorAndDrivenTrait,
} = useConsolidated3rdVariant();
const { upcData, selectedUpcId } = useUpc(props.id);
const {
  experimentFlags: { isSpacingExperimentEnabled },
} = useExperiments();

// Color swatches expandable area causes a height shift animation when # rows of colors changes
// TODO update MCL so we can remove this rerender hack
const key = ref(0);
watch(priceToColors, () => {
  key.value++;
});

const experimentFlagsObj = {
  isSpacingExperimentEnabled,
};
const store = useStore();
const availabilityData = computed(() => store.state.availabilityStore.data);

const spacingClass = isSpacingExperimentEnabled ? 'margin-bottom-xs' : 'margin-bottom-m';

const touched = ref(false);

const { memberProductsMap } = useMembersOfProduct();

const upcIdSource = computed(() => selectedUpcId.value || firstUpcKeyForColorAndDrivenTrait.value);
const selectedProductId = computed(() => upcData.value.upcs[upcIdSource.value]?.relationships.selectedProduct);

const selectedConsolidatedSizeChartId = computed(() => memberProductsMap.value[selectedProductId.value]?.traits?.sizes?.sizeChartId);
const selectedConsolidatedSizeChartImage = computed(() => memberProductsMap.value[selectedProductId.value]?.traits?.sizes?.sizeChartImg);

const isSizeChartForConsolidated = computed(() => !!(selectedConsolidatedSizeChartId.value || selectedConsolidatedSizeChartImage.value));

const { fireTagOnChange } = useTraitAnalyticsData({
  isConsolidatedMaster: false,
  isMcom,
  product: props.product,
  isConsolidatedThirdVariant: true,
});

const forceSizeRerender = ref(0);
if (isTypeDriven) {
  // force ExpandableList to rerender since it doesn't rerender when sizes length changes
  watch(selectedTypeId, () => {
    forceSizeRerender.value += 1;
  });
}

// We use @update:modelValue so it only listens to when user click on type
function onTypeChange() {
  fireTagOnChange({
    selectedUpcId: selectedUpcId.value,
    isVariant: !isTypeDriven,
    productPricingState: props.productPricingState,
  });
}

// We use @update:modelValue so it only listens to when user click on size
function onSizeChange() {
  const selectedSddData = availabilityData.value?.upcNumberSddMap || {};

  fireTagOnChange({
    selectedUpcId: selectedUpcId.value,
    isVariant: isTypeDriven,
    productPricingState: props.productPricingState,
    selectedSddData,
  });
}
</script>
